import axiosInstance from 'shared/services/api/axiosInstance';

/**
 * Issues v6 (TRO-481): the issue store, over /api/issues-v6. Its own module —
 * nothing here imports the legacy issues code, which is deleted at cutover.
 */

const team = (teamId) => ({ team_id: teamId });

export async function listIssues(teamId, { status = [], agents = [], categories = [], search = '', limit = 25, offset = 0 } = {}) {
  const params = { ...team(teamId), limit, offset };
  if (status.length) params.status = status.join(',');
  if (agents.length) params.agent = agents.join(',');
  if (categories.length) params.category = categories.join(',');
  if (search) params.search = search;
  const { data } = await axiosInstance.get('/issues-v6', { params });
  return { issues: data?.issues || [], total: data?.total || 0, agents: data?.agents || [] };
}

export async function getIssue(teamId, id) {
  const { data } = await axiosInstance.get(`/issues-v6/${id}`, { params: team(teamId) });
  return data;
}

/** What a coding agent reads over MCP: the issue with each failure's text. */
export async function getIssueAgentView(teamId, id) {
  const { data } = await axiosInstance.get(`/issues-v6/${id}/agent`, { params: { ...team(teamId), limit: 20 } });
  return data;
}

export async function setIssueStatus(teamId, id, status) {
  const { data } = await axiosInstance.post(`/issues-v6/${id}/status`, { status }, { params: team(teamId) });
  return data;
}

export async function mergeIssue(teamId, id, into) {
  const { data } = await axiosInstance.post(`/issues-v6/${id}/merge`, { into }, { params: team(teamId) });
  return data;
}

export async function editIssue(teamId, id, patch) {
  const { data } = await axiosInstance.patch(`/issues-v6/${id}`, patch, { params: team(teamId) });
  return data;
}

/** A person files an issue (from a Failures row). */
export async function fileIssue(teamId, body) {
  const { data } = await axiosInstance.post('/issues-v6', body, { params: team(teamId) });
  return data;
}

/** The server's refusal, as the person should read it. */
export const errorText = (e, fallback) => e?.response?.data?.error || e?.message || fallback;


/** Hand an issue to the team's coding agent (heal). */
export async function triggerIssueFix(teamId, { source, issueId, extraInstructions = null }) {
  const { data } = await axiosInstance.post('/heal/trigger', {
    teamId, source, issue_v6_id: issueId, extra_instructions: extraInstructions,
  });
  return data;
}

/** Every heal run for an issue, and the one in flight. */
export async function listIssueFixes(teamId, issueId) {
  const { data } = await axiosInstance.get(`/heal/issues-v6/${issueId}/sessions`, { params: { teamId } });
  return { sessions: data?.sessions || [], activeSessionId: data?.active_session_id || null };
}

/**
 * Track issues: the issue-type templates, and what the team tracks — each an
 * alert (and eval) with the triage agent that files issues.
 */
export async function getTracking(teamId) {
  const { data } = await axiosInstance.get('/issues-v6/tracking', { params: team(teamId) });
  return {
    templates: data?.templates || [], categories: data?.categories || [], windows: data?.windows || [], trackers: data?.trackers || [],
  };
}

export async function deployTracker(teamId, body) {
  const { data } = await axiosInstance.post('/issues-v6/tracking', body, { params: team(teamId) });
  return data?.tracker || null;
}

/** Edit a deployed tracker in place: { threshold, window_seconds, min_sample, slack_channel_id }. */
export async function updateTracker(teamId, id, body) {
  const { data } = await axiosInstance.patch(`/issues-v6/tracking/${encodeURIComponent(id)}`, body, { params: team(teamId) });
  return data?.tracker || null;
}

export async function setTrackerLive(teamId, id, live) {
  const { data } = await axiosInstance.post(`/issues-v6/tracking/${encodeURIComponent(id)}/live`, { live }, { params: team(teamId) });
  return data?.tracker || null;
}

/** The team's agents, busiest first: [{agent_name, run_count, error_count, last_seen_at}]. */
export async function listTeamAgents(teamId) {
  const { data } = await axiosInstance.get('/agent-runs/agents', { params: team(teamId) });
  return data?.agents || [];
}
